<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader || (!showLoader && !applyErrorState && !getOptionalNotificationPreferences.length && getNotOptionalNotificationPreferences.length)"
    :showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
	:applyErrorState="applyErrorState"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <transition name="common-enter-animation" mode="out-in" appear>
            <div v-if="!showLoader && !applyErrorState" class="content-wrapper">
                <!-- Non-optional notifications -->
                <div class="whiteBox">
                    <div class="textBox">
                        <div class="component-title">{{ t('userArea.definitions.notifications.notOptional.title') }}</div>
                        <div class="description">{{ t('userArea.definitions.notifications.notOptional.description') }}</div>
                    </div>
                    <table-generic
                    hideHeader
                    noHoverStyles
                    rowHeightClass="medium"
                    :hasDetail="false"
                    :headerData="notificationPreferencesTableHeaderData"
                    :tableStyles="tableStyles"
                    :tableData="notificationPreferencesNotOptional"
                    @toggleSwitch="switchClickHandler($event)"/>
                    <divider-generic/>
                </div>
                <!-- Ooptional notifications -->
                <div class="whiteBox">
                    <div class="textBox">
                        <div class="component-title">{{ t('userArea.definitions.notifications.optional.title') }}</div>
                        <div class="description">{{ t('userArea.definitions.notifications.optional.description') }}</div>
                    </div>
                    <table-generic
                    hideHeader
                    noHoverStyles
                    rowHeightClass="medium"
                    :hasDetail="false"
                    :headerData="notificationPreferencesTableHeaderData"
                    :tableStyles="tableStyles"
                    :tableData="notificationPreferencesOptional"
                    @toggleSwitch="switchClickHandler($event)"/>
                </div>
            </div>
		</transition>
    </wrapper-features-templates-one-column>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import DividerGeneric from '@/components/generic/dividers/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import TableGeneric from '@/components/generic/table/Generic'

/* Assets & Helpers */
import { ref, computed, onBeforeUnmount } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestNotificationPreferencesList, editNotificationStatus, clearFeatureStates } = mapActions(moduleMapper.USER_AREA_DEFINITIONS_NOTIFICATIONS)
const { getOptionalNotificationPreferences, getNotOptionalNotificationPreferences } = mapGetters(moduleMapper.USER_AREA_DEFINITIONS_NOTIFICATIONS)

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* Data */
const showOverlayLoader = ref(false)
const showLoader = ref(false)
const applyErrorState = ref(false)
const eventRow = ref()
const eventIsActive = ref()

/************************************************
*												*
*         Notification Preferences Data         *
*												*
************************************************/
const notificationPreferencesTableHeaderData = [
	{ label: '', showInMobile: { groupWith: 'status' }, dataAttr: 'translation', rowCellType: 'title-description' },
	{ label: '', showInMobile: true, dataAttr: 'type', rowCellType: 'switch' }
]

const tableStyles = [null, { 'text-align': 'end' }]

const notificationPreferencesNotOptional = computed(() => {
	return getNotOptionalNotificationPreferences.value.map((notification) => {
		return [
			{ value: notification.name, dataAttr: 'id' },
			{ type: 'title-description', value: { title: notification.translation, content: notification.description }, dataAttr: 'title-description' },
			{
				type: 'switch',
				value: { isActive: notification.allowed, disabled: !notification.optional },
				dataAttr: 'type'
			}
		]
	})
})

const notificationPreferencesOptional = computed(() => {
	return getOptionalNotificationPreferences.value.map((notification) => {
		return [
			{ value: notification.name, dataAttr: 'id' },
			{ type: 'title-description', value: { title: notification.translation, content: notification.description }, dataAttr: 'category' },
			{
				type: 'switch',
				value: { isActive: notification.allowed, disabled: !notification.optional },
				dataAttr: 'type'
			}
		]
	})
})

/************************************************
*												*
*                 Requests Logic                *
*												*
************************************************/
const requestNotificationPrefsList = async () => {
	showLoader.value = true
	cancelAllPendingRequests()
	applyErrorState.value = false
	showFeedbackToast.value = false

	try {
		await requestNotificationPreferencesList()
		showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyErrorState.value = true
		showLoader.value = false
		errorHandler(error, requestNotificationPrefsList)
	}
}

const switchClickHandler = $event => {
	eventRow.value = $event.rowId
	eventIsActive.value = $event.isActive

	setNotificationState()
}

const setNotificationState = async () => {
	showOverlayLoader.value = true

	const payload = {
		id: eventRow.value,
		status: !eventIsActive.value
	}

	try {
		const res = await editNotificationStatus(payload)
		showOverlayLoader.value = false
		successHandler(res.data)
	} catch (error) {
		showOverlayLoader.value = false
		if (error.toString() === 'Cancel') return
		errorHandler(error.data, setNotificationState)
	}
}

/************************************************
*												*
*                 Error Feedback                *
*												*
************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

/************************************************
*												*
*             Success/Error Handlers            *
*												*
************************************************/
const successHandler = data => {
	feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
	showOverlayLoader.value = false
	showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

requestNotificationPrefsList()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.content-wrapper {
	@include commonEnteringAnimation;
    max-width: 500px;
    width: 100%;

    margin: 0 auto;

    .whiteBox {
        .divider-generic-wrapper {
            margin-top: 24px;
        }
        .textBox {
            margin-bottom: 20px;
            .component-title {
                width: 100%;
                margin-bottom: 8px;
                flex-shrink: 0;

                text-align: left;
                color: var(--primary);
                font-size: 20px;
                font-weight: 500;
            }
            .description {
                font-size: 14px;
                font-weight: 400;
                color: var(--secondary-dark-1);
                line-height: 18px;
            }
        }
    }
}

</style>
